import {
  Divider,
  Form,
  InputNumber,
  message,
  Modal,
  Select,
  Switch,
  Typography,
} from 'antd'
import { mergeRoiAssumptionsForSite } from 'features/libra/roi/niiRoiModel'
import type {
  LibraTicketGroup,
  SiteRoiAssumptionsValue,
} from 'features/libra/ticket/libraTicketsApi'
import {
  TAR_ROI_GLOBAL_STORAGE_KEY,
  TAR_ROI_GROUP_DATA_KEY,
  useUpdateLibraTicketGroup,
} from 'features/libra/ticket/libraTicketsApi'
import { useGeckoPermissions } from 'features/permissions'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_LIBRA_EXECUTIVE,
} from 'types'

const { Text, Title } = Typography

type RoiFormShape = SiteRoiAssumptionsValue & { siteId: string }

type RoiAssumptionsSettingsModalProps = {
  open: boolean
  onClose: () => void
  group: LibraTicketGroup
  siteOptions: ReadonlyArray<{ value: string; label: string }>
  /** When set, that site is pre-selected; otherwise user must pick. */
  selectedSiteId: string | undefined
  onSaved: () => void
}

export const RoiAssumptionsSettingsModal: FC<
  RoiAssumptionsSettingsModalProps
> = ({ open, onClose, group, siteOptions, selectedSiteId, onSaved }) => {
  const { hasPermissions } = useGeckoPermissions()
  const canEditRoi = hasPermissions([
    PERMISSION_GECKO_INTERNAL_MEMBER,
    PERMISSION_LIBRA_EXECUTIVE,
  ])
  const doUpdate = useUpdateLibraTicketGroup()
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<RoiFormShape>()

  const defaultSite = useMemo(() => {
    if (selectedSiteId) return selectedSiteId
    return siteOptions[0]?.value
  }, [selectedSiteId, siteOptions])

  const loadFormForSite = useCallback(
    (site: string) => {
      const tar = group[TAR_ROI_GROUP_DATA_KEY] ?? {}
      const globalRaw = tar[TAR_ROI_GLOBAL_STORAGE_KEY]
      const siteRaw = tar[site]
      const a = mergeRoiAssumptionsForSite(globalRaw, siteRaw)
      form.setFieldsValue({
        siteId: site,
        traditionalInspectionTeamPerAsset: a.traditionalInspectionTeamPerAsset,
        niiPeoplePerAsset: a.niiPeoplePerAsset,
        niiRateAedPerM2: a.niiRateAedPerM2,
        hseIncidentProbPerPersonPerTar: a.hseIncidentProbPerPersonPerTar,
        hseDownsideAed: a.hseDownsideAed,
        criticalPathReductionPercent: a.criticalPathReductionPercent,
        tarDurationDays: a.tarDurationDays,
        dailyRatePerPersonAed: a.dailyRatePerPersonAed,
        dailyProductionAed: a.dailyProductionAed,
        inspectionOnCriticalPath: a.inspectionOnCriticalPath,
      })
    },
    [form, group]
  )

  useEffect(() => {
    if (!open) return
    const site = defaultSite
    if (!site) return
    loadFormForSite(site)
  }, [open, defaultSite, loadFormForSite])

  const handleSiteChange = useCallback(
    (site: string) => {
      loadFormForSite(site)
    },
    [loadFormForSite]
  )

  const onOk = useCallback(async () => {
    if (!canEditRoi) {
      message.warning('You do not have permission to edit group ROI settings.')
      return
    }
    const v = await form.validateFields()
    const siteId = v.siteId
    if (!siteId) {
      message.error('Select a site.')
      return
    }
    setSaving(true)
    try {
      const prev = group[TAR_ROI_GROUP_DATA_KEY] ?? {}
      const globalPayload: SiteRoiAssumptionsValue = {
        traditionalInspectionTeamPerAsset: v.traditionalInspectionTeamPerAsset,
        niiPeoplePerAsset: v.niiPeoplePerAsset,
        niiRateAedPerM2: v.niiRateAedPerM2,
        hseIncidentProbPerPersonPerTar: v.hseIncidentProbPerPersonPerTar,
        hseDownsideAed: v.hseDownsideAed,
        criticalPathReductionPercent: v.criticalPathReductionPercent,
      }
      const tarPayload: SiteRoiAssumptionsValue = {
        tarDurationDays: v.tarDurationDays,
        dailyRatePerPersonAed: v.dailyRatePerPersonAed,
        dailyProductionAed: v.dailyProductionAed,
        inspectionOnCriticalPath: v.inspectionOnCriticalPath,
      }
      const nextAssumptions: Record<string, SiteRoiAssumptionsValue> = {
        ...prev,
        [TAR_ROI_GLOBAL_STORAGE_KEY]: {
          ...(prev[TAR_ROI_GLOBAL_STORAGE_KEY] ?? {}),
          ...globalPayload,
        },
        [siteId]: {
          ...(prev[siteId] ?? {}),
          ...tarPayload,
        },
      }
      await doUpdate({
        group,
        next: {
          title: group.title,
          totalAssets: group.totalAssets,
          owners: group.owners,
          [TAR_ROI_GROUP_DATA_KEY]: nextAssumptions,
        },
      })
      message.success('ROI assumptions saved')
      onSaved()
      onClose()
    } catch (e) {
      console.error(e)
      message.error('Could not save ROI assumptions')
    } finally {
      setSaving(false)
    }
  }, [form, doUpdate, group, canEditRoi, onClose, onSaved])

  if (siteOptions.length === 0) {
    return (
      <Modal
        title='ROI assumptions'
        open={open}
        onCancel={onClose}
        footer={null}>
        <Text type='secondary'>Add tickets with a site tag first.</Text>
      </Modal>
    )
  }

  return (
    <Modal
      title='ROI assumptions'
      open={open}
      onCancel={onClose}
      onOk={onOk}
      okText='Save'
      okButtonProps={{ disabled: !canEditRoi, loading: saving }}
      width={560}>
      <Form form={form} layout='vertical' size='small' disabled={!canEditRoi}>
        <Title level={5} style={{ marginTop: 0 }}>
          Global assumptions
        </Title>
        <Text type='secondary'>
          Saved once for the campaign and shared across all sites.
        </Text>
        <Form.Item
          name='traditionalInspectionTeamPerAsset'
          label='Traditional inspection team per asset (people)'
          style={{ marginTop: 12 }}
          tooltip='Compared with NII people per asset to get "people saved" per eligible asset.'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='niiPeoplePerAsset'
          label='NII people per asset'
          tooltip='People still required per NII-eligible asset under the NII program.'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='niiRateAedPerM2' label='NII rate (AED / m²)'>
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='hseIncidentProbPerPersonPerTar'
          label='HSE incident probability (per person, per TAR)'>
          <InputNumber min={0} step={0.000001} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='hseDownsideAed' label='HSE downside (AED per event)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='criticalPathReductionPercent'
          label='Critical path reduction (%)'
          extra='When inspection is on the critical path: this fraction of TAR length x daily production.'>
          <InputNumber min={0} max={100} style={{ width: '100%' }} />
        </Form.Item>

        <Divider style={{ margin: '16px 0' }} />

        <Title level={5} style={{ marginBottom: 4 }}>
          Turnaround (per site)
        </Title>
        <Text type='secondary'>
          These values vary per turnaround. Select a site to view or edit its
          inputs.
        </Text>
        <Form.Item
          name='siteId'
          label='Site'
          rules={[{ required: true }]}
          style={{ marginTop: 12 }}>
          <Select
            options={[...siteOptions]}
            onChange={handleSiteChange}
            showSearch
            optionFilterProp='label'
          />
        </Form.Item>
        <Form.Item name='tarDurationDays' label='TAR duration (days)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='dailyProductionAed'
          label='Daily production value (AED / day)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='inspectionOnCriticalPath'
          label='Inspection on critical path'
          valuePropName='checked'>
          <Switch />
        </Form.Item>
        <Form.Item
          name='dailyRatePerPersonAed'
          label='Daily rate per person (AED)'
          tooltip='Used in labor saving: people saved x this rate x TAR days.'>
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
